// Which microphone the operator talks into.
//
// macOS hands the browser whatever its default input happens to be, and on a laptop docked to a
// monitor, a headset or a capture device that is routinely the WRONG one — a device that is present,
// permitted and live, and produces silence. Measured on a real call: connected, 27 ms round trip,
// zero audio energy for its whole duration. The panel can now say so; this is the control that fixes
// it without leaving the call.
//
// The choice is remembered per browser rather than per call: an operator who had to pick the right
// input once during an emergency should not have to do it again on the next one.

const DEVICE_KEY = 'gisify.sos.micDeviceId'

export interface AudioInput {
  deviceId: string
  label: string
}

/**
 * The remembered input, if any.
 *
 * Every accessor is guarded: private windows and blocked site data throw on `localStorage` itself,
 * before `getItem` is reached, and a forgotten preference must never stop a call panel rendering.
 */
export function readPreferredMic(): string | null {
  try {
    const v = window.localStorage.getItem(DEVICE_KEY)

    return v && v.trim() !== '' ? v : null
  } catch {
    return null
  }
}

export function writePreferredMic(deviceId: string | null): void {
  try {
    if (deviceId === null) window.localStorage.removeItem(DEVICE_KEY)
    else window.localStorage.setItem(DEVICE_KEY, deviceId)
  } catch {
    // Same reasoning.
  }
}

/**
 * The audio inputs this browser will admit to.
 *
 * Labels are blank until the origin has been granted microphone permission, which is why the picker
 * is only offered DURING a call — by then the grant exists and the list reads as device names rather
 * than as a row of empty strings.
 */
export async function listAudioInputs(): Promise<AudioInput[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return []

  try {
    const devices = await navigator.mediaDevices.enumerateDevices()

    return devices
      .filter(d => d.kind === 'audioinput')
      .map((d, i) => ({
        deviceId: d.deviceId,
        // A nameless device is still selectable — saying "Microphone 2" beats an empty row.
        label: d.label.trim() !== '' ? d.label : `Microphone ${i + 1}`,
      }))
  } catch {
    return []
  }
}

/**
 * Capture one specific input, falling back to the default when it has gone.
 *
 * `exact` rather than a soft preference on purpose: a soft constraint silently hands back the
 * default when the requested device is missing, which is exactly the failure the operator is here to
 * escape — they would pick a device, get the broken one anyway, and have no way to tell. When the
 * device is genuinely gone we say so by falling back deliberately, and the caller forgets it.
 */
export async function captureMic(deviceId: string | null): Promise<{ stream: MediaStream; usedPreferred: boolean }> {
  if (deviceId) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: deviceId } } })

      return { stream, usedPreferred: true }
    } catch (err) {
      // A denial is the operator's answer and must not be retried around; only a missing or
      // unusable DEVICE falls back.
      if ((err as { name?: string } | null)?.name === 'NotAllowedError') throw err
    }
  }

  return { stream: await navigator.mediaDevices.getUserMedia({ audio: true }), usedPreferred: false }
}

const GAIN_KEY = 'gisify.sos.micGain'

/** The remembered gain, or null when the operator has never changed it. */
export function readPreferredGain(): number | null {
  try {
    const raw = window.localStorage.getItem(GAIN_KEY)
    const n = raw === null ? NaN : Number(raw)

    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

export function writePreferredGain(value: number): void {
  try {
    window.localStorage.setItem(GAIN_KEY, String(value))
  } catch {
    // A forgotten gain is a nuisance; a call panel that fails to render is not.
  }
}
